import React, { useState } from 'react';
import { Plus, X, Edit3, Save, User, Phone, Mail } from 'lucide-react';

interface Person {
  id: string;
  nom: string;
  prenoms: string;
  fonction: string;
  nationalite?: string;
  telephone?: string;
  email?: string;
  adresse?: string;
  pourcentageActions?: number;
  dateNomination?: string;
  typeContrat?: string;
  salaire?: number;
  diplomes?: string[];
}

interface PersonInputProps {
  label: string;
  value: Person[];
  onSave: (persons: Person[]) => void;
  disabled?: boolean;
  placeholder?: string;
  personType?: 'dirigeant' | 'employe' | 'actionnaire';
  showEquity?: boolean;
  showContract?: boolean;
  showSalary?: boolean;
}

export const PersonInput: React.FC<PersonInputProps> = ({
  label,
  value = [],
  onSave,
  disabled = false,
  placeholder = "Ajouter une personne",
  personType = 'dirigeant',
  showEquity = false,
  showContract = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [persons, setPersons] = useState<Person[]>(value);
  const [newPerson, setNewPerson] = useState<Partial<Person>>({});

  const handleStartEditing = () => {
    setIsEditing(true);
    setPersons([...value]);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setPersons([...value]);
    setNewPerson({});
  };

  const handleSaveEditing = () => {
    onSave(persons);
    setIsEditing(false);
    setNewPerson({});
  };

  const handleAddPerson = () => {
    if (newPerson.nom && newPerson.prenoms && newPerson.fonction) {
      const person: Person = {
        id: Date.now().toString(),
        nom: newPerson.nom || '',
        prenoms: newPerson.prenoms || '',
        fonction: newPerson.fonction || '',
        nationalite: newPerson.nationalite || '',
        telephone: newPerson.telephone || '',
        email: newPerson.email || '',
        adresse: newPerson.adresse || '',
        pourcentageActions: newPerson.pourcentageActions || 0,
        dateNomination: newPerson.dateNomination || '',
        typeContrat: newPerson.typeContrat || '',
        salaire: newPerson.salaire || 0,
        diplomes: newPerson.diplomes || []
      };
      setPersons([...persons, person]);
      setNewPerson({});
    }
  };

  const handleRemovePerson = (index: number) => {
    const updatedPersons = persons.filter((_, i) => i !== index);
    setPersons(updatedPersons);
  };

  const handleEditPerson = (index: number, field: keyof Person, value: any) => {
    const updatedPersons = [...persons];
    updatedPersons[index] = { ...updatedPersons[index], [field]: value };
    setPersons(updatedPersons);
  };

  const getPersonTypeLabel = () => {
    switch (personType) {
      case 'dirigeant': return 'Dirigeant';
      case 'employe': return 'Employé';
      case 'actionnaire': return 'Actionnaire';
      default: return 'Personne';
    }
  };

  const getFonctionSuggestions = () => {
    switch (personType) {
      case 'dirigeant': 
        return ['Directeur Général', 'Président', 'Directeur Technique', 'Directeur Commercial', 'Directeur Financier'];
      case 'employe':
        return ['Développeur', 'Chef de Projet', 'Commercial', 'Comptable', 'Assistant', 'Technicien'];
      case 'actionnaire':
        return ['Actionnaire majoritaire', 'Actionnaire minoritaire', 'Fondateur', 'Investisseur'];
      default:
        return [];
    }
  };

  if (!isEditing) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
          {!disabled && (
            <button
              onClick={handleStartEditing}
              className="text-[#197ca8] hover:text-[#156a92] text-sm flex items-center"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Modifier
            </button>
          )}
        </div>
        
        {value.length === 0 ? (
          <div className="text-gray-500 text-sm italic p-4 border-2 border-dashed border-gray-200 rounded-lg text-center">
            {placeholder}
          </div>
        ) : (
          <div className="space-y-3">
            {value.map((person) => (
              <div key={person.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <User className="w-4 h-4 text-[#197ca8] mr-2" />
                      <span className="font-medium text-gray-900">
                        {person.prenoms} {person.nom}
                      </span>
                      <span className="ml-2 px-2 py-1 bg-[#197ca8] text-white text-xs rounded">
                        {person.fonction}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                      {person.telephone && (
                        <div className="flex items-center">
                          <Phone className="w-3 h-3 mr-1" />
                          {person.telephone}
                        </div>
                      )}
                      {person.email && (
                        <div className="flex items-center">
                          <Mail className="w-3 h-3 mr-1" />
                          {person.email}
                        </div>
                      )}
                      {person.nationalite && (
                        <div>Nationalité: {person.nationalite}</div>
                      )}
                      {showEquity && person.pourcentageActions && (
                        <div className="font-medium text-[#197ca8]">
                          Actions: {person.pourcentageActions}%
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleCancelEditing}
            className="text-gray-600 hover:text-gray-800 text-sm flex items-center"
          >
            <X className="w-4 h-4 mr-1" />
            Annuler
          </button>
          <button
            onClick={handleSaveEditing}
            className="text-green-600 hover:text-green-800 text-sm flex items-center"
          >
            <Save className="w-4 h-4 mr-1" />
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Liste des personnes existantes */}
      <div className="space-y-3">
        {persons.map((person, index) => (
          <div key={person.id} className="bg-white border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-gray-900">
                {getPersonTypeLabel()} #{index + 1}
              </span>
              <button
                onClick={() => handleRemovePerson(index)}
                className="text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={person.nom}
                  onChange={(e) => handleEditPerson(index, 'nom', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Prénoms *</label>
                <input
                  type="text"
                  value={person.prenoms}
                  onChange={(e) => handleEditPerson(index, 'prenoms', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fonction *</label>
                <select
                  value={person.fonction}
                  onChange={(e) => handleEditPerson(index, 'fonction', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                >
                  <option value="">Sélectionner...</option>
                  {getFonctionSuggestions().map(fonction => (
                    <option key={fonction} value={fonction}>{fonction}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nationalité</label>
                <input
                  type="text"
                  value={person.nationalite}
                  onChange={(e) => handleEditPerson(index, 'nationalite', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={person.telephone}
                  onChange={(e) => handleEditPerson(index, 'telephone', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={person.email}
                  onChange={(e) => handleEditPerson(index, 'email', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              
              {showEquity && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">% Actions</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={person.pourcentageActions}
                    onChange={(e) => handleEditPerson(index, 'pourcentageActions', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                  />
                </div>
              )}

              {showContract && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type contrat</label>
                  <select
                    value={person.typeContrat}
                    onChange={(e) => handleEditPerson(index, 'typeContrat', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="CDI">CDI</option>
                    <option value="CDD">CDD</option>
                    <option value="Consultant">Consultant</option>
                    <option value="Stage">Stage</option>
                  </select>
                </div>
              )}
            </div>

            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">Adresse</label>
              <textarea
                value={person.adresse}
                onChange={(e) => handleEditPerson(index, 'adresse', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Ajouter une nouvelle personne */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-3">
          Ajouter un nouveau {getPersonTypeLabel().toLowerCase()}
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label>
            <input
              type="text"
              value={newPerson.nom || ''}
              onChange={(e) => setNewPerson({...newPerson, nom: e.target.value})}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Prénoms *</label>
            <input
              type="text"
              value={newPerson.prenoms || ''}
              onChange={(e) => setNewPerson({...newPerson, prenoms: e.target.value})}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fonction *</label>
            <select
              value={newPerson.fonction || ''}
              onChange={(e) => setNewPerson({...newPerson, fonction: e.target.value})}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
            >
              <option value="">Sélectionner...</option>
              {getFonctionSuggestions().map(fonction => (
                <option key={fonction} value={fonction}>{fonction}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={newPerson.email || ''}
              onChange={(e) => setNewPerson({...newPerson, email: e.target.value})}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleAddPerson}
            disabled={!newPerson.nom || !newPerson.prenoms || !newPerson.fonction}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#197ca8] hover:bg-[#156a92] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
};